import React from 'react';
import styled from 'styled-components';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../shared/constants/btnNames';
import {
  type FloorPlanA4PreviewProps,
  type FloorPlanCaptionLayout,
  FLOOR_PLAN_A4_PREVIEW_INPUT_IDS,
} from './floorPlanA4PreviewTypes';

export type { FloorPlanCaptionLayout, FloorPlanA4PreviewProps };

/** 한 단(열) 고정 너비(상한) — 최대 4단이 왼쪽부터 이 너비로 채워짐 */
const FLOOR_PLAN_CAPTION_COLUMN_WIDTH = 'min(8vh, 80px)';

const FLOOR_IMAGE_INPUT_ID = FLOOR_PLAN_A4_PREVIEW_INPUT_IDS.vertical;

const FloorPlanA4PreviewVertical: React.FC<FloorPlanA4PreviewProps> = ({
  logoSrc,
  title,
  subtitle,
  date,
  imageSrc,
  gallery,
  location,
  pageNumberLabel,
  captionLayout,
  captionSegmentCounts,
  onFloorImageFileChange,
}) => {
  const handleFloorImagePickClick = () => {
    document.getElementById(FLOOR_IMAGE_INPUT_ID)?.click();
  };

  const captionRowPlaceholderLineCount =
    captionLayout === 'row'
      ? captionSegmentCounts.reduce((sum, segment) => sum + segment, 0)
      : 0;

  return (
    <A4PreviewSlot>
      <A4PaperPortrait>
        <LogoInA4 src={logoSrc} alt="" />
        <TitleAndSubTitleAndDate>
          <TitleInA4>{title}</TitleInA4>
          <SubTitleInA4>{subtitle}</SubTitleInA4>
          <DateInA4>{date}</DateInA4>
        </TitleAndSubTitleAndDate>
        <ImgWrapperInA4>
          <HiddenFileInput
            id={FLOOR_IMAGE_INPUT_ID}
            type="file"
            accept="image/*"
            onChange={onFloorImageFileChange}
          />
          <ImgInA4 src={imageSrc} alt="Floor Plan Image" />
          <BtnWrapper>
            <HeadlessBtn
              value={getBtnName('REPLACE_IMAGE')}
              handleClick={handleFloorImagePickClick}
              StyledBtn={PaperBlurFitBtn}
            />
          </BtnWrapper>
        </ImgWrapperInA4>
        {captionLayout === 'column' ? (
          <CaptionColumnsInA4>
            {captionSegmentCounts.map((blockCount, colIndex) => {
              const colStartOrdinal = captionSegmentCounts
                .slice(0, colIndex)
                .reduce((s, n) => s + n, 0);
              return (
                <CaptionColumnInA4 key={`floor-plan-caption-col-${colIndex}`}>
                  {Array.from({ length: blockCount }, (_, blockIndex) => (
                    <WorkCaptionBlockShell
                      key={`floor-plan-caption-block-${colIndex}-${blockIndex}`}
                    >
                      <CaptionPreviewOrdinal>{`${colStartOrdinal + blockIndex + 1}.`}</CaptionPreviewOrdinal>
                      <CaptionRightShell aria-hidden />
                    </WorkCaptionBlockShell>
                  ))}
                </CaptionColumnInA4>
              );
            })}
          </CaptionColumnsInA4>
        ) : (
          <CaptionRowsListInA4>
            {Array.from({ length: captionRowPlaceholderLineCount }, (_, lineIndex) => (
              <CaptionInlinePlaceholderRow key={`floor-plan-caption-row-line-${lineIndex}`}>
                <CaptionPreviewOrdinal>{`${lineIndex + 1}.`}</CaptionPreviewOrdinal>
                <CaptionRowContentBar aria-hidden />
              </CaptionInlinePlaceholderRow>
            ))}
          </CaptionRowsListInA4>
        )}
        <GalleryInA4>{gallery}</GalleryInA4>
        <LocationInA4>{location}</LocationInA4>
        <PageNumberInA4>{pageNumberLabel}</PageNumberInA4>
      </A4PaperPortrait>
    </A4PreviewSlot>
  );
};

export default FloorPlanA4PreviewVertical;

const A4PreviewSlot = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 10px;
`;

/** A4 세로(portrait) 210 : 297 */
const A4PaperPortrait = styled.div`
  position: relative;
  box-sizing: border-box;
  height: 100%;
  width: auto;
  max-width: 100%;
  aspect-ratio: 210 / 297;
  flex-shrink: 0;
  align-self: center;
  margin: 0 auto;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Alpha_04};
`;

const LogoInA4 = styled.img`
  position: absolute;
  top: 1vh;
  right: 1vh;
  width: 1.2vh;
  height: auto;
  object-fit: contain;
`;

const TitleAndSubTitleAndDate = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 1vh;
  padding-left: 1vh;
  margin-bottom: 20px;
`;

const TitleInA4 = styled.div`
  ${({ theme }) => theme.typography.Footer_01};
`;

const SubTitleInA4 = styled.div`
  ${({ theme }) => theme.typography.Footer_02};
`;

const DateInA4 = styled.span`
  ${({ theme }) => theme.typography.Footer_02};
`;

const ImgWrapperInA4 = styled.div`
  position: relative;
  width: 100%;
  padding: 0 1vh;
  margin-bottom: 20px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ImgInA4 = styled.img`
  width: 100%;
  height: 18vh;
  object-fit: contain;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
`;

const BtnWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`;

const GalleryInA4 = styled.div`
  position: absolute;
  bottom: 1vh;
  left: 1vh;
  ${({ theme }) => theme.typography.Footer_02};
`;

const LocationInA4 = styled.div`
  position: absolute;
  bottom: 1vh;
  left: 50%;
  transform: translateX(-50%);
  ${({ theme }) => theme.typography.Footer_02};
`;

const PageNumberInA4 = styled.div`
  position: absolute;
  bottom: 1vh;
  right: 1vh;
  ${({ theme }) => theme.typography.Footer_02};
`;

const CaptionColumnsInA4 = styled.div`
  padding-left: 1vh;
  padding-right: 1vh;
  margin-bottom: 20px;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: clamp(8px, 1.5vw, 14px);
  overflow: hidden;
`;

const CaptionColumnInA4 = styled.div`
  flex: 0 0 auto;
  box-sizing: border-box;
  width: ${FLOOR_PLAN_CAPTION_COLUMN_WIDTH};
  max-width: ${FLOOR_PLAN_CAPTION_COLUMN_WIDTH};
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CaptionRowsListInA4 = styled.div`
  padding-left: 1vh;
  padding-right: 1vh;
  margin-bottom: 20px;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 2px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const CaptionInlinePlaceholderRow = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  width: 100%;
  min-width: 0;
`;

const CaptionPreviewOrdinal = styled.span`
  flex: 0 0 auto;
  min-width: 1.75em;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  font-variant-numeric: tabular-nums;
`;

const CaptionRowContentBar = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  height: 6px;
  min-height: 6px;
  background: ${({ theme }) => theme.colors.color_Gray_05};
  opacity: 0.28;
`;

const WorkCaptionBlockShell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  box-sizing: border-box;
  min-width: 0;
  width: 100%;

  &:not(:last-child) {
    padding-bottom: 6px;
  }
`;

const CaptionRightShell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 70%;
  min-width: 0;
  min-height: 40px;
  background: ${({ theme }) => theme.colors.color_Gray_05};
  opacity: 0.28;
`;
